import {
  AcademicCapIcon,
  BriefcaseIcon,
  ChatIcon,
  CurrencyDollarIcon,
  GlobeAltIcon,
  ShoppingBagIcon,
} from '@heroicons/react/outline';
import useLocale from 'next-translate/useTranslation';

import type { Icon, IndustryKey } from './industry/types';

export type IndustryEntry = {
  key: IndustryKey;
  icon: Icon;
  label: string;
  summary: string;
  question: string;
  answer: string;
  href: string;
  cta: string;
};

/**
 * The six industries with a page of their own, as the main page's tabs and
 * the industry pages' cross-links describe them. One list, so a label never
 * reads differently in two places.
 */
export function useIndustryCatalog(): IndustryEntry[] {
  const { t } = useLocale('chatbots');

  return [
    {
      key: 'customer-service',
      icon: ChatIcon,
      label: t('Customer service'),
      summary: t(
        'Answers questions about orders, accounts and policies around the clock, on every channel your customers use.',
      ),
      question: t('Where is my order #4821?'),
      answer: t(
        'It left our warehouse yesterday and arrives on Thursday. Here is your tracking link.',
      ),
      href: '/chatbots/customer-service',
      cta: t('Explore customer service agents'),
    },
    {
      key: 'sales',
      icon: CurrencyDollarIcon,
      label: t('Sales'),
      summary: t(
        'Qualifies visitors, answers pricing questions and books demos straight into your calendar.',
      ),
      question: t('Do you have a plan for teams?'),
      answer: t(
        'Yes, from five seats. Shall I book you a 15-minute demo this week?',
      ),
      href: '/chatbots/sales',
      cta: t('Explore sales agents'),
    },
    {
      key: 'ecommerce',
      icon: ShoppingBagIcon,
      label: t('E-commerce'),
      summary: t(
        'Helps shoppers find the right product, explains shipping and returns, and sends them to checkout.',
      ),
      question: t('Will these boots fit a wide foot?'),
      answer: t(
        'Most customers with wide feet go half a size up. Here are our wide-fit styles.',
      ),
      href: '/chatbots/ecommerce',
      cta: t('Explore e-commerce agents'),
    },
    {
      key: 'travel',
      icon: GlobeAltIcon,
      label: t('Travel and hospitality'),
      summary: t(
        'Handles bookings, check-in questions and local tips, in the guest’s own language.',
      ),
      question: t('Can I bring a surfboard on the airport shuttle?'),
      answer: t(
        'Yes, boards travel free on our shuttle. I’ve added it to your booking.',
      ),
      href: '/chatbots/travel',
      cta: t('Explore travel agents'),
    },
    {
      key: 'education',
      icon: AcademicCapIcon,
      label: t('Education'),
      summary: t(
        'Guides students through admissions, courses and deadlines at any time of day.',
      ),
      question: t('When do applications close for the autumn intake?'),
      answer: t(
        'On 30 June. Would you like the checklist of documents you need?',
      ),
      href: '/chatbots/education',
      cta: t('Explore education agents'),
    },
    {
      key: 'hr',
      icon: BriefcaseIcon,
      label: t('HR'),
      summary: t(
        'Answers employee questions about leave, benefits and policies straight from your handbook.',
      ),
      question: t('How do I request parental leave?'),
      answer: t(
        'Send the form in the HR portal at least eight weeks ahead. Here is the link.',
      ),
      href: '/chatbots/hr',
      cta: t('Explore HR agents'),
    },
  ];
}
